import { ResponseMESF } from "@dexteel/mesf-core";
import { Box, Paper } from "@mui/material";
import { Column, GridReadyEvent, ColumnResizedEvent } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useMemo, useState } from "react";
import { CenteredLazyLoading } from "../../../../../../controls/LazyLoading";
import { AgGridFooterActions } from "../../../../../../shared/data-entries/AgGridFooterActions";
import { useDataEntryContext } from "../../../context/reducer.provider";
import { useTimerContext } from "../../../context/timer.provider";
import { useEAFDataEntryActions } from "../../../hooks/useEAFDataEntryActions";
import { readChargesFromPLC } from "../../../repositories/eaf.repositories";
import { ChargeModal } from "../../modals/charge/ChargeModal";
import { useTheme } from "@mui/material/styles";
import { eafDETablesStyles } from "../styles/eafDETablesStyles";
import { useTableData } from "./hooks/useChargesDataTable";
import { Grid } from "@mui/material";
import { defaultColDef, defaultGridOptions } from "@/setup/ag-grid/mesf-theme";

export const TableCharges = () => {
  const theme = useTheme();
  const styles = eafDETablesStyles(theme);

  const { pause, resume } = useTimerContext();

  const {
    state: { deProcess },
    actions: { setNotificationMessage, setErrorMessage },
  } = useDataEntryContext();
  const { deleteCharge, refreshData } = useEAFDataEntryActions();

  const [showModal, setShowModal] = useState<"NEW" | "EDIT" | "">("");
  const [selectedCharge, setSelectedCharge] = useState<any>();
  const [columnData, setColumnData] = useState<
    { colId: string | undefined; width: number }[]
  >([]);

  const charges = deProcess?.charges || [];

  const totals = useMemo(
    () => ({
      weight: charges.reduce((sum, charge) => sum + (charge.Weight || 0), 0),
      lime: charges.reduce((sum, charge) => sum + (charge.Lime || 0), 0),
    }),
    [charges],
  );

  const readAutomaticCharges = async (value?: string) => {
    if (value === "OK") {
      const resp = await readChargesFromPLC({
        ProcessId: deProcess!.process!.ProcessId,
      });
      if (resp.ok) {
        setNotificationMessage("Read charges from PLC successful");
        await refreshData();
        return resp as ResponseMESF;
      } else {
        setErrorMessage(resp.message);
        return {
          ok: false,
          message: "Read charges from PLC failed",
        } as ResponseMESF;
      }
    } else {
      return {} as ResponseMESF;
    }
  };

  const editCharge = (ChargeId: number) => {
    pause();
    const charge = charges?.find((c) => c.ChargeId === ChargeId);
    setSelectedCharge(charge);
    setShowModal("EDIT");
  };

  const { columns } = useTableData({
    deleteCharge,
    editCharge,
  });

  return (
    <>
      <Paper>
        <Box padding={1} sx={styles.agGridBoxWithTotals}>
          <AgGridReact
            rowData={charges}
            columnDefs={columns}
            defaultColDef={{ ...defaultColDef, sortable: false }}
            loadingOverlayComponent={CenteredLazyLoading}
            onGridReady={(params: GridReadyEvent) => {
              const columns = params.api.getAllDisplayedColumns();
              const newColumnData = columns?.map((col: Column) => ({
                colId: col.getColId(),
                width: col.getActualWidth(),
              }));
              setColumnData(newColumnData || []);
            }}
            onColumnResized={(params: ColumnResizedEvent) => {
              const columns = params.api.getAllDisplayedColumns();
              const newColumnData = columns?.map((col: Column) => ({
                colId: col.getColId(),
                width: col.getActualWidth(),
              }));
              setColumnData(newColumnData || []);
            }}
            {...defaultGridOptions}
          />
          <Grid sx={styles.totalsRow}>
            {columnData.map((col, index) => (
              <Grid
                key={col.colId}
                sx={styles.totalCell}
                style={{ width: `${col.width}px`, minWidth: `${col.width}px` }}
              >
                {index === 2
                  ? "Totals"
                  : col.colId === "Weight"
                    ? totals.weight.toFixed(0)
                    : col.colId === "Lime"
                      ? totals.lime.toFixed(0)
                      : ""}
              </Grid>
            ))}
          </Grid>
          <AgGridFooterActions
            setShowModal={setShowModal}
            readFromPLC={readAutomaticCharges}
            table="charges"
            pause={pause}
          />
        </Box>
      </Paper>
      <ChargeModal
        showModal={showModal}
        charge={selectedCharge}
        onHide={() => {
          resume();
          setShowModal("");
        }}
      />
    </>
  );
};
